import { useState, useEffect, type RefObject } from 'react';

export interface CanvasDimensions {
  width: number;
  height: number;
  aspectRatio: number;
  isPortrait: boolean;
  vbWidth: number;
  vbHeight: number;
}

const BASE_HEIGHT = 600;

export function useResponsiveCanvas(containerRef: RefObject<HTMLElement | null>): CanvasDimensions {
  const [dimensions, setDimensions] = useState<CanvasDimensions>(() => {
    const defaultW = typeof window !== 'undefined' ? window.innerWidth : 960;
    const defaultH = typeof window !== 'undefined' ? Math.max(Math.round(window.innerHeight * 0.55), 320) : 540;
    const ratio = defaultW / (defaultH || 1);
    const vbW = Math.max(360, Math.round(BASE_HEIGHT * ratio));
    return {
      width: defaultW,
      height: defaultH,
      aspectRatio: ratio,
      isPortrait: ratio < 1.05,
      vbWidth: vbW,
      vbHeight: BASE_HEIGHT,
    };
  });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let rafId: number | null = null;

    const measure = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const w = rect.width > 0 ? rect.width : (typeof window !== 'undefined' ? window.innerWidth : 960);
        const h = rect.height > 0 ? rect.height : (typeof window !== 'undefined' ? Math.max(Math.round(window.innerHeight * 0.55), 320) : 540);
        const ratio = w / (h || 1);
        const vbW = Math.max(360, Math.round(BASE_HEIGHT * ratio));

        setDimensions((prev) => {
          if (Math.abs(prev.width - w) < 2 && Math.abs(prev.height - h) < 2) {
            return prev;
          }
          return {
            width: w,
            height: h,
            aspectRatio: ratio,
            isPortrait: ratio < 1.05,
            vbWidth: vbW,
            vbHeight: BASE_HEIGHT,
          };
        });
      });
    };

    measure();
    window.addEventListener('resize', measure, { passive: true });

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        measure();
      });
      ro.observe(el);
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [containerRef]);

  return dimensions;
}
